import Vue from 'vue';

const normalizeComponent = (component) => (component || 'financialclose').toLowerCase();

const normalizeProfileResponse = (rawData) => {
    const entries = Array.isArray(rawData) ? rawData : rawData ? [rawData] : [];
    const profileData = entries.find(entry => entry && entry.shortcutPreferences) || entries[0] || {};
    const shortcutPreferences = profileData.shortcutPreferences || {};
    // Use maxPinCount if present, else fallback to pinLimit, else 5
    const pinLimit = shortcutPreferences.maxPinCount ?? shortcutPreferences.pinLimit ?? profileData.pinLimit ?? 5;
    const preferenceId = shortcutPreferences._id || null;
    return { profileData, shortcutPreferences, pinLimit, preferenceId };
};

const resolveComponentShortcuts = (shortcutPreferences, componentKey) => {
    let componentShortcuts = [];

    if (Array.isArray(shortcutPreferences.pins)) {
        const legacyEntry = shortcutPreferences.pins.find(
            entry => entry && entry.component && entry.component.toLowerCase() === componentKey
        );
        if (legacyEntry) {
            if (Array.isArray(legacyEntry.preferences)) {
                componentShortcuts = legacyEntry.preferences;
            } else if (Array.isArray(legacyEntry.preference)) {
                componentShortcuts = legacyEntry.preference;
            }
        }
    }

    if (!componentShortcuts.length) {
        const directEntry = shortcutPreferences[componentKey];
        if (Array.isArray(directEntry)) {
            componentShortcuts = directEntry;
        }
    }

    return componentShortcuts;
};

const normalizeShortcutsList = (componentShortcuts, pinLimit, fallbackId, componentKey) => {
    if (!Array.isArray(componentShortcuts)) return [];

    const effectiveLimit = pinLimit === -1 ? componentShortcuts.length : pinLimit;

    return componentShortcuts
        .slice()
        .sort((a, b) => {
            const getStamp = (item) => new Date(item?.timestamp || item?.updatedAt || item?.createdAt || 0).getTime();
            return getStamp(b) - getStamp(a);
        })
        .slice(0, effectiveLimit)
        .map(item => ({
            ...item,
            _id: item._id || fallbackId || `${componentKey}-${item.name}`
        }));
};

export default {
    namespaced: true,
    state: () => ({
        items: {},
        pinLimits: {},
        preferenceIds: {},
        loadingStates: {},
        removingStates: {}
    }),
    mutations: {
        setLoading(state, { component, value }) {
            Vue.set(state.loadingStates, component, !!value);
        },
        setRemoving(state, { component, value }) {
            if (value) {
                Vue.set(state.removingStates, component, value);
            } else {
                Vue.delete(state.removingStates, component);
            }
        },
        setData(state, { component, shortcuts, pinLimit, preferenceId }) {
            Vue.set(state.items, component, Array.isArray(shortcuts) ? shortcuts : []);
            if (pinLimit !== undefined) {
                Vue.set(state.pinLimits, component, pinLimit);
            }
            if (preferenceId !== undefined) {
                Vue.set(state.preferenceIds, component, preferenceId);
            }
        }
    },
    actions: {
        async fetchShortcuts({ commit, state }, { component } = {}) {
            const componentKey = normalizeComponent(component);
            commit('setLoading', { component: componentKey, value: true });
            try {
                const response = await Vue.axios.post('/v2/profile/getdetails');
                const rawData = response?.data?.data;
                const { shortcutPreferences, pinLimit, preferenceId } = normalizeProfileResponse(rawData);

                if (pinLimit === -1) {
                    commit('setData', { component: componentKey, shortcuts: [], pinLimit, preferenceId });
                    return { success: true, data: { shortcuts: [], pinLimit, disabled: true } };
                }

                const componentShortcuts = resolveComponentShortcuts(shortcutPreferences, componentKey);
                const shortcuts = normalizeShortcutsList(componentShortcuts, pinLimit, preferenceId, componentKey);

                commit('setData', { component: componentKey, shortcuts, pinLimit, preferenceId });
                return { success: true, data: { shortcuts, pinLimit, disabled: false } };
            } catch (error) {
                const existingShortcuts = state.items[componentKey] || [];
                const existingLimit = state.pinLimits[componentKey];
                const existingPreference = state.preferenceIds[componentKey];
                commit('setData', {
                    component: componentKey,
                    shortcuts: existingShortcuts,
                    pinLimit: existingLimit,
                    preferenceId: existingPreference
                });
                return {
                    success: false,
                    message: error?.response?.data?.message || error?.message || 'Failed to load shortcuts.'
                };
            } finally {
                commit('setLoading', { component: componentKey, value: false });
            }
        },
        async addShortcut({ state, dispatch, rootState }, { component, shortcut }) {
            const componentKey = normalizeComponent(component);
            const pinLimit = typeof state.pinLimits[componentKey] === 'number'
                ? state.pinLimits[componentKey]
                : Number.POSITIVE_INFINITY;
            const shortcuts = state.items[componentKey] || [];

            if (pinLimit === -1) {
                return { success: false, type: 'warning', message: 'Shortcuts are disabled for your account.' };
            }

            if (shortcuts.length >= pinLimit) {
                return { success: false, type: 'warning', message: 'You have reached your shortcut limit. Remove one to add another.' };
            }

            if (shortcut && shortcuts.some(item => item.url === shortcut.url)) {
                return { success: false, type: 'info', message: 'This shortcut is already pinned.' };
            }

            try {
                const payload = {
                    client: rootState.client?._id,
                    data: {
                        pins: [{
                            component: componentKey,
                            preference: [{
                                name: shortcut.name,
                                url: shortcut.url,
                                meta: shortcut.meta || {}
                            }]
                        }]
                    }
                };

                await Vue.axios.post('/v2/user-preferences/pin', payload);
                await dispatch('fetchShortcuts', { component: componentKey });

                return { success: true, type: 'success', message: 'Shortcut added successfully.' };
            } catch (error) {
                return {
                    success: false,
                    type: 'error',
                    message: error?.response?.data?.message || error?.message || 'Failed to add shortcut.'
                };
            }
        },
        async removeShortcut({ commit, dispatch }, { component, shortcut }) {
            const componentKey = normalizeComponent(component);
            if (!shortcut || !shortcut.name) {
                return { success: false, type: 'error', message: 'Invalid shortcut.' };
            }

            const removalKey = shortcut._id || `${shortcut.name}-${shortcut.url || ''}`;
            commit('setRemoving', { component: componentKey, value: removalKey });

            try {
                const payloadData = { component: componentKey, pinName: shortcut.name };
                if (shortcut._id) {
                    await Vue.axios.post(`/v2/user-preferences/unpin/${shortcut._id}`, { data: payloadData });
                } else {
                    await Vue.axios.post('/v2/user-preferences/unpin', { data: [payloadData] });
                }

                await dispatch('fetchShortcuts', { component: componentKey });
                return { success: true, type: 'success', message: 'Shortcut removed successfully.' };
            } catch (error) {
                return {
                    success: false,
                    type: 'error',
                    message: error?.response?.data?.message || error?.message || 'Failed to remove shortcut.'
                };
            } finally {
                commit('setRemoving', { component: componentKey, value: null });
            }
        }
    },
    getters: {
        componentShortcuts: (state) => (component) => {
            const componentKey = normalizeComponent(component);
            return state.items[componentKey] || [];
        },
        pinLimit: (state) => (component) => {
            const componentKey = normalizeComponent(component);
            return state.pinLimits[componentKey];
        },
        isLoading: (state) => (component) => {
            const componentKey = normalizeComponent(component);
            return !!state.loadingStates[componentKey];
        },
        removingKey: (state) => (component) => {
            const componentKey = normalizeComponent(component);
            return state.removingStates[componentKey] || null;
        },
        hasShortcuts: (state, getters) => (component) => {
            return getters.componentShortcuts(component).length > 0;
        },
        isDisabled: (state, getters) => (component) => {
            return getters.pinLimit(component) === -1;
        },
        componentSummary: (state, getters) => (component) => {
            return {
                shortcuts: getters.componentShortcuts(component),
                pinLimit: getters.pinLimit(component),
                loading: getters.isLoading(component),
                disabled: getters.isDisabled(component),
                removingKey: getters.removingKey(component)
            };
        }
    }
};
